import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useMCampaignStore } from "@stores/mCampaign";
import { useMAudienceStore } from "@stores/mAudience/mAudience";
import { getRegionOptions } from "@utils/constants/regions";
import { useGoogleMaps } from "@utils/composables/googleMaps";

export const useLocationsStore = defineStore("locations", () => {
  const mAudiencesStore = useMAudienceStore();
  const { getMAudience } = storeToRefs(mAudiencesStore);
  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign } = storeToRefs(mCampaignStore);
  const { geocodeLocation, loadGoogleMaps, isGoogleMapsLoaded } = useGoogleMaps();

  // segmentation
  const getMAudienceSegmentByLocation = computed(() => getMAudience.value.segment_by_location);
  const updateMAudienceSegmentByLocation = (value) => (getMAudience.value.segment_by_location = value);

  // country
  const getMAudienceCountry = computed(() => getMAudience.value.t_geo?.country);
  const getCountryOptions = computed(() => getRegionOptions());
  const updateMAudienceCountry = (country) => {
    getMAudience.value.t_geo.country = country;
    initMAudienceLocations();
  };

  // locations
  const locations = ref([]);
  const getCampaignAdAccount = computed(() => currentMCampaign.value.resources.facebook.adaccount);
  const getMAudienceLocations = computed(() => getMAudience.value.t_geo?.locations);
  const getMAudienceLocationRadius = (index) => parseInt(getMAudienceLocations.value[index].radius, 10);
  const updateMAudienceLocationRadius = (index, value) =>
    (getMAudienceLocations.value[index].radius = value.toString());
  const getLocationOptions = computed(() =>
    locations.value?.map((location) => {
      let locationLabel = location.name;
      if (location.region != null) locationLabel += ` (${location.region})`;
      return { label: locationLabel, value: location.key, type: location.type };
    }),
  );
  const initMAudienceLocations = () => (getMAudience.value.t_geo.locations = []);
  const removeLocation = (index) => getMAudience.value.t_geo.locations.splice(index, 1);
  const removeAllLocations = () => (getMAudience.value.t_geo.locations = []);

  const updateMAudienceLocations = async (value) => {
    if (!getMAudienceLocations.value) initMAudienceLocations();
    const selectedLocation = locations.value?.find((l) => l.key === value);
    if (selectedLocation) {
      locations.value = [];
      if (!isGoogleMapsLoaded.value) await loadGoogleMaps();
      await geocodeLocation(selectedLocation);
      getMAudience.value.t_geo.locations.push(selectedLocation);
    }
  };

  const updateLocationOptions = async (search) => {
    try {
      const url = `/facebook/${getCampaignAdAccount.value}/locations?country_code=${getMAudienceCountry.value}&q=${search}`;
      const { response, data } = await useMyFetch(url).json();

      if (response.value.ok)
        locations.value = data.value.data?.map((location) => ({ ...location, label: location.name, showOnMap: true }));
    } catch (err) {
      handleError(err);
    }
  };

  const addAllRegions = async () => {
    bulkAddLoading.value = true;
    try {
      const url = `/facebook/${getCampaignAdAccount.value}/regions?country_code=${getMAudienceCountry.value}`;
      const { response, data } = await useMyFetch(url).json();
      if (response.value.ok) {
        if (!getMAudienceLocations.value) initMAudienceLocations();
        const formattedLocations = await Promise.all(
          data.value.data.map(async (location) => {
            await geocodeLocation(location);
            return { ...location, label: location.name, showOnMap: true };
          }),
        );
        getMAudience.value.t_geo.locations = getMAudience.value.t_geo.locations.concat(formattedLocations);
      }
    } catch (err) {
      handleError(err);
    }
    bulkAddLoading.value = false;
  };

  const isLocationSelected = (key) => getMAudienceLocations.value?.find((l) => l.key === key) != null;
  const canHaveRadius = (index) => ["city", "custom_location"].includes(getMAudienceLocations.value[index].type);
  const hasRadius = (index) => getMAudienceLocations.value[index].has_radius === true;
  const createRadius = (index) => {
    getMAudienceLocations.value[index].has_radius = true;
    if (getMAudienceLocations.value[index].type === "city") getMAudienceLocations.value[index].radius = 17;
  };
  const removeRadius = (index) => (getMAudienceLocations.value[index].has_radius = false);

  // bulkAdd
  const bulkAddParams = ref({ type: "city", has_radius: true, radius: 17, locations: "" });
  const bulkAddErrors = ref([]);
  const bulkAddLocations = ref([]);
  const bulkAddLoading = ref(false);
  const bulkAddKey = ref(null);

  const resetBulkAddErrors = () => (bulkAddErrors.value = []);
  const resetParams = () => (bulkAddParams.value = { type: "city", has_radius: true, radius: 17, locations: "" });

  const addLocationsFacebook = async (locations) => {
    if (!locations) return;
    resetBulkAddErrors();
    bulkAddLoading.value = true;

    bulkAddLocations.value = locations;
    try {
      const url = `/facebook/${getCampaignAdAccount.value}/locations_batch_post`;
      const { response, data } = await useMyFetch(url)
        .post({ country_code: getMAudienceCountry.value, locations: locations })
        .json();
      if (response.value.ok) bulkAddKey.value = data.value.key;
    } catch (err) {
      bulkAddErrors.value.push("Bulk add failed, please try again");
      handleError(err);
    }
  };

  const addLocationsGoogle = async (locations) => {
    if (!locations) return;
    resetBulkAddErrors();
    bulkAddLoading.value = true;

    const geocodedLocations = await Promise.all(
      locations.map(async (location) => {
        const geocodedLocation = await geocodeLocation(location);
        return { ...geocodedLocation, label: location };
      }),
    );

    if (!getMAudienceLocations.value) initMAudienceLocations();

    const formattedLocations = geocodedLocations
      .map((location) => {
        if (location.geocoded == null) {
          bulkAddErrors.value.push(location.label);
          return null;
        } else {
          const latitude = location.geocoded.geometry.location.lat();
          const longitude = location.geocoded.geometry.location.lng();
          return {
            ...location,
            type: "custom_location",
            name: location.geocoded.formatted_address,
            label: location.geocoded.formatted_address || `(${latitude}, ${longitude})`,
            latitude,
            longitude,
            has_radius: bulkAddParams.value.has_radius,
            radius: bulkAddParams.value.has_radius ? bulkAddParams.value.radius : null,
            batch: true,
            showOnMap: true,
          };
        }
      })
      .filter((location) => {
        return (
          location != null &&
          getMAudienceLocations.value.find(
            (l) => l.latitude === location.latitude && l.longitude === location.longitude,
          ) == null
        );
      });

    formattedLocations.forEach((location) => getMAudience.value.t_geo.locations.push(location));
    resetParams();
    bulkAddLoading.value = false;
  };

  // adsChannel
  const adsChannelHandlers = (cable) => {
    return {
      connected: subscribeToChannel(cable),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable) => () => cable.perform({ channel: "AdsChannel", action: "subscribe" });

  const handleReceivedMessage = async (message) => {
    if (message.type !== "BulkAddLocations") return;
    if (message.unique_key !== bulkAddKey.value) return;

    bulkAddKey.value = null; // reset key
    if (message.error) {
      bulkAddErrors.value.push("Bulk add failed, please try again");
      bulkAddLoading.value = false;
      return;
    }

    if (!getMAudienceLocations.value) initMAudienceLocations();

    const formattedLocations = message.locations
      .map((location, i) => {
        if (location == null) {
          const faultyLocation = bulkAddLocations.value.at(i);
          bulkAddErrors.value.push(faultyLocation);
        } else {
          return {
            ...location,
            has_radius: bulkAddParams.value.has_radius,
            radius: bulkAddParams.value.has_radius ? bulkAddParams.value.radius : null,
            label: location.name,
            batch: true,
            showOnMap: true,
          };
        }
      })
      .filter((location) => {
        return location != null && getMAudienceLocations.value.find((l) => l.key === location.key) == null;
      });

    const geocodedLocations = await Promise.all(formattedLocations.map(async (location) => geocodeLocation(location)));

    geocodedLocations.forEach((location) => getMAudience.value.t_geo.locations.push(location));
    resetParams();
    bulkAddLoading.value = false;
  };

  return {
    // segmentation
    getMAudienceSegmentByLocation,
    updateMAudienceSegmentByLocation,
    // country
    getMAudienceCountry,
    getCountryOptions,
    updateMAudienceCountry,
    // locations
    locations,
    getMAudienceLocations,
    getLocationOptions,
    getMAudienceLocationRadius,
    updateMAudienceLocationRadius,
    removeLocation,
    removeAllLocations,
    updateMAudienceLocations,
    updateLocationOptions,
    addAllRegions,
    isLocationSelected,
    canHaveRadius,
    hasRadius,
    createRadius,
    removeRadius,
    // bulkAdd
    bulkAddParams,
    bulkAddErrors,
    bulkAddLoading,
    bulkAddKey,
    addLocationsFacebook,
    addLocationsGoogle,
    resetParams,
    resetBulkAddErrors,
    // adsChannel
    adsChannelHandlers,
  };
});
